import { APP_KEYS, MIN_PASSWORD_LENGTH } from '@iac/contracts';
import type { AppKey, UserWithAccessDto } from '@iac/contracts';
import { useState, type FormEvent } from 'react';
import { DataTable, Td, Tr } from '../../components/DataTable';
import { DeleteButton } from '../../components/DeleteButton';
import { Dialog } from '../../components/Dialog';
import { Field } from '../../components/Field';
import { AppIconFor, IconPencilSquare, IconPlus } from '../../components/icons';
import { PageHeader } from '../../components/PageHeader';
import { useDict } from '../../i18n/dictionary';
import { shell } from '../../i18n/shell';
import { useAuth } from '../../lib/auth';
import { usePageTitle } from '../../lib/page';
import { useCreateUser, useDeleteUser, useSetAccess, useUsers } from './api';
import { adminDict } from './i18n';

type NewUserValues = {
  login: string;
  password: string;
  passwordConfirm: string;
};

/**
 * Creating and editing alike. A new user also needs a login and password;
 * an existing one only has their grants changed — there is no password reset
 * here.
 */
function UserForm({
  user,
  pending,
  onCreate,
  onSetAccess,
  onCancel,
}: {
  user?: UserWithAccessDto;
  pending: boolean;
  onCreate: (values: NewUserValues & { apps: AppKey[] }) => void;
  onSetAccess: (apps: AppKey[]) => void;
  onCancel: () => void;
}) {
  const t = useDict(adminDict);
  const appNames = useDict(shell).appNames;
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [apps, setApps] = useState<AppKey[]>(user?.apps ?? []);

  function toggle(app: AppKey, granted: boolean) {
    setApps((current) =>
      granted ? [...current, app] : current.filter((a) => a !== app),
    );
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (user) onSetAccess(apps);
    else onCreate({ login, password, passwordConfirm, apps });
  }

  return (
    <form onSubmit={submit} className="form-stack">
      {!user && (
        <>
          <Field
            id="user-login"
            label={t.login}
            name="login"
            type="text"
            value={login}
            onChange={(e) => setLogin(e.target.value)}
            autoComplete="off"
            autoFocus
            required
          />
          <Field
            id="user-password"
            label={t.password}
            name="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            minLength={MIN_PASSWORD_LENGTH}
            required
          />
          <Field
            id="user-password-confirm"
            label={t.confirmPassword}
            name="passwordConfirm"
            type="password"
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            autoComplete="new-password"
            minLength={MIN_PASSWORD_LENGTH}
            required
          />
        </>
      )}
      <fieldset>
        <legend className="field-label">{t.apps}</legend>
        <div className="user-form-apps">
          {APP_KEYS.map((app) => (
            <label key={app} className="user-form-app">
              <input
                type="checkbox"
                name="apps"
                value={app}
                className="checkbox-violet"
                checked={apps.includes(app)}
                onChange={(e) => toggle(app, e.target.checked)}
              />
              {appNames[app]}
            </label>
          ))}
        </div>
      </fieldset>
      <div className="form-actions">
        <button type="button" className="btn-ghost" onClick={onCancel}>
          {t.cancel}
        </button>
        <button type="submit" className="btn-primary" disabled={pending}>
          {user ? t.save : t.create}
        </button>
      </div>
    </form>
  );
}

/** The granted apps as icons, in `APP_KEYS` order whatever order they came in. */
function GrantIcons({ apps }: { apps: AppKey[] }) {
  const appNames = useDict(shell).appNames;
  return (
    <div className="user-apps">
      {APP_KEYS.filter((app) => apps.includes(app)).map((app) => (
        <span
          key={app}
          role="img"
          aria-label={appNames[app]}
          title={appNames[app]}
        >
          <AppIconFor appKey={app} />
        </span>
      ))}
    </div>
  );
}

function UserRow({
  entry,
  canDelete,
  onEdit,
}: {
  entry: UserWithAccessDto;
  canDelete: boolean;
  onEdit: () => void;
}) {
  const t = useDict(adminDict);
  const remove = useDeleteUser();

  return (
    <Tr id={`user-row-${entry.user.id}`}>
      <Td emphasis>
        {entry.user.login}
        {/* Below md the grants column is hidden and the icons sit under the
            login instead, where they get the row's width. */}
        <div className="user-apps-under-login">
          <GrantIcons apps={entry.apps} />
        </div>
      </Td>
      <Td>
        <GrantIcons apps={entry.apps} />
      </Td>
      <Td>
        <div className="row-actions">
          <button
            type="button"
            className="btn-icon-ghost"
            aria-label={t.editAccess(entry.user.login)}
            title={t.editAccess(entry.user.login)}
            onClick={onEdit}
          >
            <IconPencilSquare />
          </button>
          {canDelete && (
            <DeleteButton
              label={t.deleteUser(entry.user.login)}
              confirm={t.confirmDelete(entry.user.login)}
              onConfirm={() => remove.mutate(entry.user.id)}
            />
          )}
        </div>
      </Td>
    </Tr>
  );
}

export function AdminUsers() {
  const t = useDict(adminDict);
  usePageTitle(t.usersTitle);
  const { session } = useAuth();
  const { data: users } = useUsers();
  const create = useCreateUser();
  const setAccess = useSetAccess();
  const [dialog, setDialog] = useState<
    null | { mode: 'add' } | { mode: 'edit'; entry: UserWithAccessDto }
  >(null);
  const close = () => setDialog(null);
  const editing = dialog?.mode === 'edit' ? dialog.entry : undefined;

  return (
    <>
      <PageHeader
        title={t.users}
        actions={
          <button
            type="button"
            className="btn-primary-icon"
            aria-label={t.createUser}
            title={t.createUser}
            onClick={() => setDialog({ mode: 'add' })}
          >
            <IconPlus />
          </button>
        }
      />

      <DataTable
        columns={[
          { label: t.login },
          { label: t.apps, priority: 'secondary' },
          { align: 'center' },
        ]}
        empty={t.noUsers}
      >
        {users &&
          users.length > 0 &&
          users.map((entry) => (
            <UserRow
              key={entry.user.id}
              entry={entry}
              // The server refuses self-deletion with 403; hide the button
              // rather than offering an action that cannot succeed.
              canDelete={entry.user.id !== session?.user.id}
              onEdit={() => setDialog({ mode: 'edit', entry })}
            />
          ))}
      </DataTable>

      <Dialog
        open={dialog !== null}
        title={editing ? t.editAccess(editing.user.login) : t.createUser}
        onClose={close}
      >
        <UserForm
          key={editing?.user.id ?? 'new'}
          user={editing}
          pending={editing ? setAccess.isPending : create.isPending}
          onCreate={(values) => create.mutate(values, { onSuccess: close })}
          onSetAccess={(apps) =>
            editing &&
            setAccess.mutate(
              { id: editing.user.id, apps },
              { onSuccess: close },
            )
          }
          onCancel={close}
        />
      </Dialog>
    </>
  );
}
